import { useEffect, useRef, useState, FormEvent, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Me, type ConfigField, type AdminAuthStatus, type AdminAuthProbeResult, type AdminAuthHealth } from '../lib/api';

type Categories = Record<string, { label: string; fields: ConfigField[] }>;

export default function Configuration({ me }: { me: Me }) {
  // No hardcoded tenant preset — default to the signed-in admin's own tenant.
  // A super-admin can type any tenant id into the picker.
  const [tenantId, setTenantId] = useState<string>(me.tenant_id);
  const [cats, setCats] = useState<Categories | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ioMsg, setIoMsg] = useState<string | null>(null);
  const [busyIO, setBusyIO] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try { setErr(null); const r = await api.configList(tenantId); setCats(r.categories); }
    catch (e: any) { setErr(e.message); setCats(null); }
  }
  useEffect(() => { load(); }, [tenantId]);

  // Export: structure + non-secret values → downloadable XML (secrets never leave).
  async function handleExport() {
    setBusyIO(true); setIoMsg(null); setErr(null);
    try {
      const data = await api.configExport(tenantId);
      const esc = (s: string) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      let xml = `<?xml version="1.0" encoding="UTF-8"?>\n<trinethub-config tenant="${esc(data.tenant_id)}" exported="${esc(data.exported_at)}">\n`;
      for (const [catKey, cat] of Object.entries(data.categories)) {
        xml += `  <category name="${esc(catKey)}" label="${esc(cat.label)}">\n`;
        for (const f of cat.fields) {
          if (f.secret) {
            xml += `    <field name="${esc(f.name)}" secret="true" configured="${f.configured}"/>  <!-- value not exported; set per tenant -->\n`;
          } else {
            xml += `    <field name="${esc(f.name)}" secret="false" configured="${f.configured}">${esc(f.value ?? '')}</field>\n`;
          }
        }
        xml += `  </category>\n`;
      }
      xml += `</trinethub-config>\n`;
      const blob = new Blob([xml], { type: 'application/xml' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `trinethub-config-${data.tenant_id}.xml`;
      a.click(); URL.revokeObjectURL(a.href);
      setIoMsg(`Exported ${data.tenant_id} config (non-secret values only — secrets are never exported).`);
    } catch (e: any) { setErr(`Export failed: ${e.message}`); }
    finally { setBusyIO(false); }
  }

  // Import: apply non-secret values from an XML file to the CURRENT tenant.
  async function handleImportFile(file: File) {
    setBusyIO(true); setIoMsg(null); setErr(null);
    try {
      const text = await file.text();
      const doc = new DOMParser().parseFromString(text, 'application/xml');
      if (doc.querySelector('parsererror')) throw new Error('Not a valid XML file.');
      const srcTenant = doc.documentElement.getAttribute('tenant') || '(unknown)';
      const fields = Array.from(doc.querySelectorAll('category > field'));
      let applied = 0; const secretsToSet: string[] = [];
      for (const el of fields) {
        const cat = el.parentElement?.getAttribute('name') || '';
        const name = el.getAttribute('name') || '';
        const secret = el.getAttribute('secret') === 'true';
        const value = el.textContent ?? '';
        if (!cat || !name) continue;
        if (secret) { if (el.getAttribute('configured') === 'true') secretsToSet.push(`${cat}.${name}`); continue; }
        if (value.trim() === '') continue;
        await api.configUpsert({ tenant_id: tenantId, category: cat, field: name, value });
        applied++;
      }
      await load();
      const secMsg = secretsToSet.length ? ` Now enter these secrets for ${tenantId}: ${secretsToSet.join(', ')}.` : '';
      setIoMsg(`Imported ${applied} non-secret field(s) from "${srcTenant}" into "${tenantId}".${secMsg}`);
    } catch (e: any) { setErr(`Import failed: ${e.message}`); }
    finally { setBusyIO(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Configuration Manager</h1>
          <p className="text-gray-500 text-sm mt-1">
            Credentials for on-prem Cisco/Webex systems. Stored AES-GCM encrypted in D1; never visible to the
            client. Pulled by the spoke on startup via HMAC-signed POST <span className="font-mono">/api/spokes/config</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm flex-wrap">
          {me.role === 'super_admin' && (
            <>
              <span className="text-gray-500">Tenant:</span>
              <input value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                     className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent w-40 font-mono" />
            </>
          )}
          {(me.role === 'super_admin' || me.role === 'tenant_admin') && (
            <>
              <button onClick={handleExport} disabled={busyIO}
                      title="Download this tenant's config as XML (non-secret values only — secrets are never exported)"
                      className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/40 disabled:opacity-50">⤓ Export XML</button>
              <button onClick={() => fileRef.current?.click()} disabled={busyIO}
                      title="Import an exported XML to pre-fill this tenant's non-secret fields"
                      className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/40 disabled:opacity-50">⤒ Import XML</button>
              <input ref={fileRef} type="file" accept=".xml,application/xml,text/xml" className="hidden"
                     onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); }} />
            </>
          )}
        </div>
      </div>

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}
      {ioMsg && <div className="card p-3 text-sm text-tnh-ok">{ioMsg}</div>}

      {/* Webex Control Hub admin OAuth — unlocks HISTORICAL Analyzer / flow
          detail / LGW CSR endpoints that the Service-App OAuth scopes can not
          reach. Shown first because it's the prerequisite for those features. */}
      {(me.role === 'tenant_admin' || me.role === 'super_admin') && (
        <AdminAuthCard me={me} />
      )}


      {cats === null && !err && <div className="card p-8 text-center text-gray-500">Loading…</div>}

      <div className="space-y-4">
        {cats && Object.entries(cats).map(([catKey, cat]) => (
          <CategoryCard key={catKey} me={me} tenantId={tenantId} catKey={catKey} cat={cat} onChange={load} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// WebexInventoryCard — Section B. Two tabs (Workspaces / Devices) backed by
// admin-token D1 sync. Search + paginated D1 reads. The "Refresh" button
// triggers a live sync against webexapis.com — auto-nightly cron handles
// the unattended path.
// ============================================================================
export function WebexInventoryCard() {
  const [tab, setTab] = useState<'workspaces' | 'devices'>('workspaces');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [workspaces, setWorkspaces] = useState<import('../lib/api').WebexWorkspace[]>([]);
  const [devices, setDevices] = useState<import('../lib/api').WebexDevice[]>([]);
  const [locNames, setLocNames] = useState<Record<string, string>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  async function load() {
    setBusy(true);
    try {
      const [ws, dv] = await Promise.all([
        api.webexWorkspaces(500, 0).catch(() => ({ items: [] as import('../lib/api').WebexWorkspace[], total: 0 })),
        api.webexDevices(500, 0).catch(() => ({ items: [] as import('../lib/api').WebexDevice[], total: 0 })),
      ]);
      setWorkspaces(ws.items); setDevices(dv.items);
    } catch (e: any) { setSyncMsg({ kind: 'err', text: e?.message ?? 'load failed' }); }
    finally { setBusy(false); }
  }
  useEffect(() => {
    load();
    api.wxcLocations().then((r) => {
      const mm: Record<string, string> = {};
      for (const l of r.locations ?? []) mm[l.id] = l.name;
      setLocNames(mm);
    }).catch(() => {});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const locName = (id: string | null) => id ? (locNames[id] ?? '(unmapped location)') : 'No location';
  const wsNames: Record<string, string> = {};
  for (const w of workspaces) wsNames[w.workspace_id] = w.display_name || w.workspace_id;

  const t = q.trim().toLowerCase();
  const fWs = workspaces.filter((w) => !t ||
    (w.display_name || '').toLowerCase().includes(t) ||
    locName(w.location_id).toLowerCase().includes(t) ||
    (w.type || '').toLowerCase().includes(t));
  const fDv = devices.filter((d) => !t ||
    (d.display_name || '').toLowerCase().includes(t) ||
    (d.product || '').toLowerCase().includes(t) ||
    (d.ip || '').toLowerCase().includes(t) ||
    (d.mac || '').toLowerCase().includes(t) ||
    (d.serial || '').toLowerCase().includes(t) ||
    (wsNames[d.workspace_id || ''] || '').toLowerCase().includes(t));

  const grouped: Record<string, typeof fWs> = {};
  for (const w of fWs) { const k = locName(w.location_id); (grouped[k] = grouped[k] || []).push(w); }
  const groupKeys = Object.keys(grouped).sort();

  const online = devices.filter((d) => d.connection_status === 'connected').length;
  const offline = devices.filter((d) => !!d.connection_status && d.connection_status !== 'connected').length;
  const unknownCount = devices.length - online - offline;
  const locCount = new Set(workspaces.map((w) => w.location_id).filter(Boolean)).size;

  function exportCsv() {
    const esc = (v: any) => { const x = v == null ? '' : String(v); return /[",\n]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x; };
    let head: string[]; let rows: string[][];
    if (tab === 'workspaces') {
      head = ['Name', 'Location', 'Capacity', 'Type', 'Last synced'];
      rows = fWs.map((w) => [w.display_name || w.workspace_id, locName(w.location_id), String(w.capacity ?? ''), w.type || '', new Date(w.last_synced_at).toISOString()]);
    } else {
      head = ['Name', 'Product', 'Type', 'IP', 'MAC', 'Serial', 'Workspace', 'Status', 'Last synced'];
      rows = fDv.map((d) => [d.display_name || d.device_id, d.product || '', d.type || '', d.ip || '', d.mac || '', d.serial || '', wsNames[d.workspace_id || ''] || '', d.connection_status || '', new Date(d.last_synced_at).toISOString()]);
    }
    const csv = [head.join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'webex_' + tab + '_' + new Date().toISOString().slice(0, 10) + '.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  async function refresh() {
    setSyncBusy(true); setSyncMsg(null);
    try {
      const r = tab === 'workspaces' ? await api.webexSyncWorkspaces() : await api.webexSyncDevices();
      setSyncMsg({ kind: r.ok ? 'ok' : 'err', text: r.ok ? ('Synced ' + r.upserted + ' ' + r.kind + ' across ' + r.pages + ' page(s).') : ('Sync failed - HTTP ' + r.last_status + (r.error ? ' - ' + r.error : '')) });
      if (r.ok) await load();
    } catch (e: any) { setSyncMsg({ kind: 'err', text: e?.message ?? 'sync failed' }); }
    finally { setSyncBusy(false); }
  }

  const shortId = (x: string | null) => x ? '...' + x.slice(-8) : '-';

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-tnh-accent font-semibold">webex inventory</div>
          <div className="font-medium mt-0.5">Workspaces &amp; Devices</div>
          <div className="text-xs text-gray-500 mt-1 max-w-2xl">
            Synced from <span className="font-mono">webexapis.com/v1</span> with the admin token. Auto-syncs nightly; <strong>Refresh</strong> to pull now.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-md border border-tnh-border dark:border-tnh-border-dark overflow-hidden text-xs">
            <button onClick={() => setTab('workspaces')} className={'px-3 py-1 ' + (tab === 'workspaces' ? 'bg-tnh-accent text-white' : '')}>Workspaces {workspaces.length}</button>
            <button onClick={() => setTab('devices')} className={'px-3 py-1 ' + (tab === 'devices' ? 'bg-tnh-accent text-white' : '')}>Devices {devices.length}</button>
          </div>
          <button onClick={exportCsv} className="btn-ghost text-xs" title="Export the current tab (filtered) as CSV">Export CSV</button>
          <button onClick={refresh} disabled={syncBusy} className="btn-ghost text-xs">{syncBusy ? 'Refreshing...' : 'Refresh'}</button>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs">
        {tab === 'workspaces' ? (
          <span className="text-gray-500">{workspaces.length} workspaces across {locCount} location(s)</span>
        ) : (
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>{online} online</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500"></span>{offline} offline</span>
            {unknownCount > 0 && <span className="inline-flex items-center gap-1 text-gray-500"><span className="w-2 h-2 rounded-full bg-gray-400"></span>{unknownCount} unknown</span>}
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder={tab === 'workspaces' ? 'Search name, location, type...' : 'Search name, IP, MAC, serial, product, workspace...'}
               className="px-3 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-80" />
        {busy && <span className="text-xs text-gray-500">Loading...</span>}
        {t && <span className="text-xs text-gray-500">{tab === 'workspaces' ? fWs.length : fDv.length} match</span>}
      </div>
      {syncMsg && <div className={'mt-2 text-xs ' + (syncMsg.kind === 'ok' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>{syncMsg.text}</div>}

      <div className="mt-3 border border-tnh-border dark:border-tnh-border-dark rounded-md overflow-auto max-h-[30rem]">
        {tab === 'workspaces' ? (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
              <tr>{['Name', 'Capacity', 'Type', 'Last synced'].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody>
              {fWs.length === 0 && !busy && (<tr><td colSpan={4} className="px-3 py-6 text-center text-gray-500">No workspaces - click <strong>Refresh</strong> to pull from Webex.</td></tr>)}
              {groupKeys.map((g) => (
                <Fragment key={g}>
                  <tr className="bg-gray-100/70 dark:bg-gray-800/50 cursor-pointer" onClick={() => setCollapsed((c) => ({ ...c, [g]: !c[g] }))}>
                    <td colSpan={4} className="px-3 py-1.5 font-semibold">{collapsed[g] ? '[+]' : '[-]'} {g} <span className="text-gray-500 font-normal">- {grouped[g].length}</span></td>
                  </tr>
                  {!collapsed[g] && grouped[g].map((w) => (
                    <tr key={w.workspace_id} className="border-t border-tnh-border dark:border-tnh-border-dark">
                      <td className="px-3 py-1.5 pl-6 font-medium">{w.display_name || shortId(w.workspace_id)}</td>
                      <td className="px-3 py-1.5">{w.capacity ?? '-'}</td>
                      <td className="px-3 py-1.5">{w.type || '-'}</td>
                      <td className="px-3 py-1.5 text-[11px] text-gray-500">{new Date(w.last_synced_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
              <tr>{['Name', 'Product', 'IP', 'MAC', 'Serial', 'Workspace', 'Status', 'Last synced'].map((h) => <th key={h} className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody>
              {fDv.length === 0 && !busy && (<tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">No devices - click <strong>Refresh</strong> to pull from Webex.</td></tr>)}
              {fDv.map((d) => (
                <tr key={d.device_id} className="border-t border-tnh-border dark:border-tnh-border-dark">
                  <td className="px-3 py-1.5 font-medium">{d.display_name || shortId(d.device_id)}</td>
                  <td className="px-3 py-1.5">{d.product || '-'}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{d.ip || '-'}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{d.mac || '-'}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">{d.serial || '-'}</td>
                  <td className="px-3 py-1.5">{d.workspace_id ? (wsNames[d.workspace_id] ?? shortId(d.workspace_id)) : '-'}</td>
                  <td className="px-3 py-1.5"><span className={'text-[10px] px-2 py-0.5 rounded-full ' + (d.connection_status === 'connected' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : d.connection_status ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300')}>{d.connection_status || 'unknown'}</span></td>
                  <td className="px-3 py-1.5 text-[11px] text-gray-500">{new Date(d.last_synced_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function AdminAuthCard({ me: _me }: { me: Me }) {
  const [status, setStatus] = useState<AdminAuthStatus | null>(null);
  const [health, setHealth] = useState<AdminAuthHealth | null>(null);
  const [healthBusy, setHealthBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [probe, setProbe] = useState<AdminAuthProbeResult | null>(null);
  const [probeBusy, setProbeBusy] = useState(false);
  const [showProbe, setShowProbe] = useState(false);
  const [params, setParams] = useSearchParams();

  const banner = params.get('admin_auth');
  const reason = params.get('reason');
  // Webex's /callback redirect now carries the full error_description and
  // trackingId — surface them in the banner so the next handshake attempt
  // can be debugged without server log diving. Falls back to status.last_connect_error
  // when the user lands on the page later and the URL params have rotated out.
  const reasonDescription = params.get('description');
  const reasonTrackingId  = params.get('trackingId');

  async function reload() {
    try { setErr(null); setStatus(await api.adminAuthStatus()); }
    catch (e: any) { setErr(e.message); }
  }

  // Section 0 kill-switch test — admins see live token usability at a glance.
  // Only call when the SPA already believes the tenant is connected; otherwise
  // there's no point pinging webexapis.com with no token. Re-run after a fresh
  // connect succeeds so the banner flips green without a page refresh.
  async function refreshHealth() {
    setHealthBusy(true);
    try { setHealth(await api.adminAuthHealth()); }
    catch (e: any) { setHealth({ ok: false, reason: 'fetch_failed', message: e.message }); }
    finally { setHealthBusy(false); }
  }

  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (status?.connected) refreshHealth();
    else setHealth(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.connected]);

  // Clear admin_auth query params once we've read them, so refresh doesn't replay.
  useEffect(() => {
    if (banner) {
      const t = setTimeout(() => {
        const next = new URLSearchParams(params);
        next.delete('admin_auth'); next.delete('reason');
        next.delete('description'); next.delete('trackingId');
        setParams(next, { replace: true });
      }, 30_000);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner]);

  async function connect() {
    setBusy(true); setErr(null);
    try {
      const r = await api.adminAuthStart();
      // Open in same tab — Webex sends a full-page redirect to its consent
      // screen, then back to /api/admin-auth/callback on the SPA. A popup
      // would also work, but full-page keeps the flow visible to the admin.
      window.location.href = r.auth_url;
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }

  async function disconnect() {
    if (!confirm('Disconnect Webex Control Hub admin OAuth for this tenant? HISTORICAL Analyzer templates and atlas-a / LGW CSR endpoints will revert to "requires admin auth" until reconnected.')) return;
    setBusy(true); setErr(null);
    try { await api.adminAuthDisconnect(); await reload(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function runProbe() {
    setProbeBusy(true); setShowProbe(true);
    try { setProbe(await api.adminAuthProbe()); }
    catch (e: any) { setProbe({ ok: false, error: 'probe_failed', message: e.message } as AdminAuthProbeResult); }
    finally { setProbeBusy(false); }
  }

  const connected = !!status?.connected;
  const cfg = !!status?.integration_configured;

  return (
    <div className="card p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-tnh-accent font-semibold">admin-oauth</div>
          <div className="font-medium mt-0.5">Webex Control Hub admin sign-in</div>
          <div className="text-xs text-gray-500 mt-1 max-w-2xl">
            Separate from the Webex Service App credentials below. Required for HISTORICAL Analyzer report templates,
            flow detail, and Webex Calling LGW CSR retrieval — features whose Cisco-side endpoints are gated by a
            Control Hub admin session, not by any Service-App OAuth scope.
          </div>
        </div>
        <div className="text-xs">
          {connected
            ? <span className="pill-ok">connected</span>
            : cfg
              ? <span className="pill-warn">not connected</span>
              : <span className="pill-warn">integration not configured</span>}
        </div>
      </div>

      {banner === 'connected' && (
        <div className="mt-3 text-xs text-tnh-ok bg-emerald-500/10 border border-emerald-500/30 rounded-md p-2">
          Connected to Webex Control Hub. Token stored encrypted; HISTORICAL templates will now try the admin path before falling back.
        </div>
      )}
      {(() => {
        // Prefer the URL params (just-now redirect) over the persisted row.
        const code        = banner === 'error' ? (reason ?? '') : (status?.last_connect_error?.code ?? '');
        const description = banner === 'error' ? (reasonDescription ?? '') : (status?.last_connect_error?.description ?? '');
        const trackingId  = banner === 'error' ? (reasonTrackingId  ?? null) : (status?.last_connect_error?.trackingId ?? null);
        const at          = banner === 'error' ? null : (status?.last_connect_error?.at ?? null);
        if (!code) return null;
        return (
          <div className="mt-3 text-xs text-tnh-err bg-red-500/10 border border-red-500/30 rounded-md p-2 space-y-1">
            <div>
              <span className="font-semibold">Connect failed: {code}</span>
              {description && <> — <span className="italic">{description}</span></>}
            </div>
            {trackingId && (
              <div className="text-[11px] opacity-80">
                trackingId: <code className="font-mono">{trackingId}</code>
              </div>
            )}
            {at && !banner && (
              <div className="text-[11px] opacity-60">recorded {new Date(at).toLocaleString()}</div>
            )}
            {code === 'invalid_scope' && (
              <div className="text-[11px] opacity-80">
                Webex rejected at least one requested scope. The Integration record at developer.webex.com only
                allows scopes ticked on its <span className="font-mono">Scopes</span> page — remove or re-tick the
                scope named in the description above, then click <strong>Connect Webex Control Hub</strong> again.
              </div>
            )}
          </div>
        );
      })()}
      {err && <div className="mt-3 text-xs text-tnh-err">{err}</div>}

      {/* Section 0 health — live kill-switch test against webexapis.com/v1/people/me.
          Only shown when the SPA already thinks we're connected; otherwise the
          banner above the card already covers the "no token" case. */}
      {connected && (
        <div className="mt-3 text-xs">
          {healthBusy && !health && (
            <div className="bg-gray-500/10 border border-gray-500/30 rounded-md p-2 text-gray-600 dark:text-gray-300">
              Checking admin-token health against <span className="font-mono">webexapis.com/v1/people/me</span>…
            </div>
          )}
          {health && health.ok && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-2 text-tnh-ok flex items-start justify-between gap-2 flex-wrap">
              <div className="space-y-0.5">
                <div>
                  <span className="font-semibold">✓ Admin token live.</span>{' '}
                  Signed in as <span className="font-mono">{health.person.displayName || (health.person.emails[0] ?? '—')}</span>
                  {' '}for org <span className="font-mono">{health.person.orgId ?? '—'}</span>.
                </div>
                <div className="text-[11px] opacity-80">
                  Token expires in {Math.floor((health.expires_in_sec ?? 0) / 60)} min · scopes: <span className="font-mono">{health.scopes || '(none)'}</span>
                </div>
              </div>
              <button onClick={refreshHealth} disabled={healthBusy} className="text-[11px] underline">Re-test</button>
            </div>
          )}
          {health && !health.ok && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-md p-2 text-tnh-err space-y-0.5">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="font-semibold">
                  ✗ Admin token health check failed
                  {typeof health.http_status === 'number' && health.http_status > 0
                    ? ` — HTTP ${health.http_status}`
                    : health.reason ? ` — ${health.reason}` : ''}
                </div>
                <button onClick={refreshHealth} disabled={healthBusy} className="text-[11px] underline">Re-test</button>
              </div>
              {health.message && <div className="text-[11px] opacity-90">{health.message}</div>}
              {health.error_snippet && (
                <pre className="font-mono text-[11px] whitespace-pre-wrap bg-white/40 dark:bg-black/30 p-1.5 rounded max-h-32 overflow-auto">{health.error_snippet}</pre>
              )}
              {health.trackingId && (
                <div className="text-[11px] opacity-80">trackingId: <code className="font-mono">{health.trackingId}</code></div>
              )}
              <div className="text-[11px] opacity-80">
                Admin-OAuth-backed features (flow names, workspaces/devices, LGW CSR) will not work until this returns ✓.
              </div>
            </div>
          )}
        </div>
      )}

      {!cfg && (
        <div className="mt-3 text-xs text-gray-500">
          Server is missing <span className="font-mono">WEBEX_INTEGRATION_*</span> secrets. See <span className="font-mono">docs/admin-oauth-setup.md</span>.
        </div>
      )}

      {cfg && (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="border border-tnh-border dark:border-tnh-border-dark rounded-lg p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Integration</span><span className="font-mono">{status?.integration_name ?? '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Redirect URI</span><span className="font-mono truncate ml-2 max-w-[60%]" title={status?.redirect_uri ?? ''}>{status?.redirect_uri ?? '—'}</span></div>
            {connected && (
              <>
                <div className="flex justify-between"><span className="text-gray-500">Connected by</span><span className="font-mono">{status?.connected_by_email ?? '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Connected at</span><span>{status?.connected_at ? new Date(status.connected_at).toLocaleString() : '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Access token</span><span className={status?.access_token_valid ? 'text-tnh-ok' : 'text-tnh-warn'}>{status?.access_token_valid ? 'valid' : 'expired (auto-refresh)'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Access expires</span><span>{status?.expires_at ? new Date(status.expires_at).toLocaleString() : '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Refresh expires</span><span>{status?.refresh_expires_at ? new Date(status.refresh_expires_at).toLocaleString() : '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Last refresh</span><span>{status?.last_refresh_at ? new Date(status.last_refresh_at).toLocaleString() : 'never'}</span></div>
              </>
            )}
          </div>
          <div className="border border-tnh-border dark:border-tnh-border-dark rounded-lg p-3 text-xs space-y-2">
            <div className="text-gray-500">Scopes granted</div>
            <div className="font-mono text-[11px] break-words whitespace-pre-wrap">
              {connected ? (status?.scopes_granted || '(none)') : '— (sign in to populate)'}
            </div>
            <div className="text-[11px] text-gray-400 mt-2">
              Webex returns the subset of requested scopes the admin granted. The post-connect probe reveals which scopes actually unlock data.
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {!connected && cfg && (
          <button onClick={connect} disabled={busy} className="btn-primary text-xs">
            {busy ? 'Opening Webex…' : 'Connect Webex Control Hub'}
          </button>
        )}
        {connected && (
          <>
            <button onClick={runProbe} disabled={probeBusy} className="btn-primary text-xs">
              {probeBusy ? 'Probing…' : 'Re-run post-connect probe'}
            </button>
            <button onClick={connect} disabled={busy} className="btn-ghost text-xs">Re-authorise</button>
            <button onClick={disconnect} disabled={busy} className="btn-ghost text-xs text-tnh-err">Disconnect</button>
          </>
        )}
        <button onClick={reload} disabled={busy} className="btn-ghost text-xs ml-auto">Refresh</button>
      </div>

      {showProbe && probe && (
        <div className="mt-4 border-t border-tnh-border dark:border-tnh-border-dark pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-gray-500">Probe results</div>
            <button onClick={() => setShowProbe(false)} className="btn-ghost text-xs">Hide</button>
          </div>
          {!probe.ok ? (
            <div className="text-xs text-tnh-err">{probe.error}: {probe.message ?? ''}</div>
          ) : (
            <>
              <div className="text-xs text-gray-500 mb-2">
                {probe.summary?.winners_count}/{probe.summary?.total_candidates} candidates returned 2xx — by group: {Object.entries(probe.summary?.winners_by_group ?? {}).map(([k, v]) => `${k}: ${v}`).join(' · ')}
              </div>
              <div className="overflow-auto max-h-96 border border-tnh-border dark:border-tnh-border-dark rounded-md">
                <table className="w-full text-[11px] font-mono">
                  <thead className="text-left text-gray-500">
                    <tr><th className="p-1.5">Group</th><th className="p-1.5">Method</th><th className="p-1.5">URL</th><th className="p-1.5">Status</th><th className="p-1.5">Snippet</th></tr>
                  </thead>
                  <tbody>
                    {probe.probes?.map((p, i) => (
                      <tr key={i} className="border-t border-tnh-border/40 dark:border-tnh-border-dark/40">
                        <td className="p-1.5">{p.group}</td>
                        <td className="p-1.5">{p.method}</td>
                        <td className="p-1.5 truncate max-w-[24rem]" title={p.url}>{p.url}</td>
                        <td className={`p-1.5 ${(p.http_status ?? 0) >= 200 && (p.http_status ?? 0) < 300 ? 'text-tnh-ok' : 'text-tnh-err'}`}>{p.http_status}</td>
                        <td className="p-1.5 truncate max-w-[24rem]" title={p.body_first_200 ?? p.error ?? ''}>{(p.body_first_200 ?? p.error ?? '').slice(0, 120)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryCard({ me, tenantId, catKey, cat, onChange }: {
  me: Me; tenantId: string; catKey: string;
  cat: { label: string; fields: ConfigField[] };
  onChange: () => void;
}) {
  const configuredCount = cat.fields.filter((f) => f.configured).length;
  const totalCount = cat.fields.length;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-tnh-accent font-semibold">{catKey}</div>
          <div className="font-medium mt-0.5">{cat.label}</div>
        </div>
        <div className="text-xs text-gray-500">
          {configuredCount === 0 ? <span className="pill-warn">not configured</span>
           : configuredCount === totalCount ? <span className="pill-ok">complete ({configuredCount}/{totalCount})</span>
           : <span className="pill-warn">partial ({configuredCount}/{totalCount})</span>}
        </div>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {cat.fields.map((f) => (
          <FieldRow key={f.name} me={me} tenantId={tenantId} catKey={catKey} f={f} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}

function FieldRow({ me, tenantId, catKey, f, onChange }: {
  me: Me; tenantId: string; catKey: string; f: ConfigField; onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!value) return;
    setBusy(true); setErr(null);
    try {
      await api.configUpsert({ tenant_id: tenantId, category: catKey, field: f.name, value });
      setValue(''); setEditing(false); setRevealed(null);
      onChange();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function del() {
    if (!confirm(`Delete "${f.label}" for tenant ${tenantId}?`)) return;
    setBusy(true);
    try { await api.configDelete(tenantId, catKey, f.name); onChange(); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }
  async function reveal() {
    setBusy(true);
    try { const r = await api.configReveal(tenantId, catKey, f.name); setRevealed(r.value); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="border border-tnh-border dark:border-tnh-border-dark rounded-lg p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-gray-500">{f.label} {f.is_secret && <span className="ml-1 text-[10px] uppercase opacity-60">secret</span>}</div>
        {f.configured && <span className="text-[10px] text-tnh-ok uppercase tracking-wide">set · v{f.version}</span>}
      </div>
      {editing ? (
        <form onSubmit={save} className="mt-2 space-y-2">
          <input type={f.is_secret ? 'password' : 'text'} value={value} onChange={(e) => setValue(e.target.value)}
                 placeholder={f.placeholder ?? `Enter ${f.label}`} autoFocus
                 className="w-full px-3 py-2 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setEditing(false); setValue(''); }} className="btn-ghost text-xs">Cancel</button>
            <button type="submit" disabled={busy || !value} className="btn-primary text-xs">{busy ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      ) : (
        <div className="mt-2 flex items-center gap-2 text-sm">
          {f.configured ? (
            <span className="font-mono text-gray-600 dark:text-gray-300 flex-1 truncate">{revealed ?? '••••••••••••'}</span>
          ) : (
            <span className="text-gray-400 italic flex-1">not set</span>
          )}
          <div className="flex gap-1">
            {f.configured && me.role === 'super_admin' && !revealed && (
              <button onClick={reveal} disabled={busy} className="btn-ghost text-xs">Reveal</button>
            )}
            {revealed && <button onClick={() => setRevealed(null)} className="btn-ghost text-xs">Hide</button>}
            <button onClick={() => { setEditing(true); setValue(''); }} className="btn-ghost text-xs text-tnh-accent">{f.configured ? 'Update' : 'Set'}</button>
            {f.configured && <button onClick={del} disabled={busy} className="btn-ghost text-xs text-tnh-err">×</button>}
          </div>
        </div>
      )}
      {f.configured && f.last_modified_by && (
        <div className="text-[10px] text-gray-400 mt-1">last set by {f.last_modified_by}{f.last_modified_at && ` · ${new Date(f.last_modified_at).toLocaleString()}`}</div>
      )}
      {err && <div className="text-xs text-tnh-err mt-1">{err}</div>}
    </div>
  );
}
